import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { CopyRow } from '../lib/clipboard.js';
import { portForwardCommand, sshCommand } from './VpnGatewayDetail.js';

export interface VpnConnectHelpModalProps {
  visible: boolean;
  onDismiss: () => void;
  /** Selected gateway's VPN address — commands render with real values when present. */
  vpnIp?: string;
  /** Hub EC2 instance id for the env (from the BFF's peer status; config.json's vpnHubInstanceId is a fallback). */
  hubInstanceId?: string;
}

const WINDOWS_PLUGIN = 'winget install Amazon.SessionManagerPlugin';
const MACOS_PLUGIN = 'brew install --cask session-manager-plugin';
const UBUNTU_PLUGIN =
  'curl "https://s3.amazonaws.com/session-manager-downloads/plugin/latest/ubuntu_64bit/session-manager-plugin.deb" -o session-manager-plugin.deb && sudo dpkg -i session-manager-plugin.deb';
const SSO_LOGIN = 'aws sso login --profile vxdev';
const LOCAL_SSH = 'ssh -p 2222 admin@127.0.0.1';

/**
 * "How do I connect?" reference for the Gateway enrollment page: prerequisites (AWS CLI, Session
 * Manager plugin per OS, SSO login) and the two-terminal port-forward flow, with copyable commands.
 * Commands use the selected gateway's real values when one is selected, placeholders otherwise.
 */
export function VpnConnectHelpModal({ visible, onDismiss, vpnIp, hubInstanceId }: VpnConnectHelpModalProps) {
  const ip = vpnIp ?? '<vpn-ip>';
  const hub = hubInstanceId ?? '<hub-instance-id>';

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header="Connecting to a gateway over the VPN"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            Close
          </Button>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <div>
          <Header variant="h3">Prerequisites (one-time)</Header>
          <SpaceBetween size="s">
            <Box fontSize="body-s" color="text-body-secondary">
              1. AWS CLI v2 with the <code>vxdev</code> profile configured, and a fresh SSO session:
            </Box>
            <CopyRow value={SSO_LOGIN} label="SSO login command" />
            <Box fontSize="body-s" color="text-body-secondary">
              2. The AWS Session Manager plugin — install for the shell you connect from, then{' '}
              <strong>open a new terminal</strong> (open shells don't see the updated PATH). WSL counts
              as Ubuntu: the Windows install does not cover WSL's Linux-side AWS CLI, and vice versa.
            </Box>
            <div>
              <Box fontSize="body-s">Windows (PowerShell):</Box>
              <CopyRow value={WINDOWS_PLUGIN} label="Windows plugin install" />
            </div>
            <div>
              <Box fontSize="body-s">Ubuntu / WSL:</Box>
              <CopyRow value={UBUNTU_PLUGIN} label="Ubuntu/WSL plugin install" />
            </div>
            <div>
              <Box fontSize="body-s">macOS (Homebrew):</Box>
              <CopyRow value={MACOS_PLUGIN} label="macOS plugin install" />
            </div>
          </SpaceBetween>
        </div>

        <div>
          <Header variant="h3">Connect (two terminals)</Header>
          <SpaceBetween size="s">
            <Box fontSize="body-s" color="text-body-secondary">
              Terminal 1 — open the tunnel through the VPN hub and leave it running (wait for{' '}
              <code>Waiting for connections...</code>):
            </Box>
            <CopyRow value={portForwardCommand(hub, ip)} label="port-forward command" />
            <Box fontSize="body-s" color="text-body-secondary">
              Terminal 2 — SSH (or scp) to the gateway through the local port, using the gateway's
              local admin credentials:
            </Box>
            <CopyRow value={LOCAL_SSH} label="local SSH command" />
            <Box fontSize="body-s" color="text-body-secondary">
              Alternative with a direct route into the VPN (e.g. from the hub itself):{' '}
              <code>{sshCommand(ip)}</code>
            </Box>
          </SpaceBetween>
        </div>

        <div>
          <Header variant="h3">Troubleshooting</Header>
          <SpaceBetween size="xs">
            <Box fontSize="body-s">
              <strong>"SessionManagerPlugin is not found"</strong> — install the plugin for that shell
              (see prerequisites) and open a new terminal. A Windows install does not fix WSL.
            </Box>
            <Box fontSize="body-s">
              <strong>"Invalid JSON: Expecting property name…"</strong> — an old copied command with
              JSON parameters; Windows PowerShell strips its quotes. Re-copy from this console — the
              commands now use shorthand parameters that work in PowerShell and bash.
            </Box>
            <Box fontSize="body-s">
              <strong>"The security token included in the request is expired"</strong> — rerun{' '}
              <code>{SSO_LOGIN}</code>.
            </Box>
            <Box fontSize="body-s">
              <strong>"TargetNotConnected"</strong> — the hub's SSM agent isn't reachable; check the
              hub instance before debugging your side.
            </Box>
          </SpaceBetween>
        </div>
      </SpaceBetween>
    </Modal>
  );
}
